import React, { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { heroMarquee, heroBackgroundImage, siteMeta } from "../data/mock";
import StudioCamera3D from "./StudioCamera3D";

const Hero = () => {
  const ref = useRef(null);
  const [tilt, setTilt] = useState({ rx: 10, ry: -8 });

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"]
  });
  const yBg = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const handleManualTilt = (dir) => {
    setTilt(prev => {
      let next = { ...prev };
      if (dir === "up") next.rx = prev.rx + 20;
      if (dir === "down") next.rx = prev.rx - 20;
      if (dir === "left") next.ry = prev.ry - 20;
      if (dir === "right") next.ry = prev.ry + 20;
      if (dir === "reset") return { rx: 10, ry: -8 };
      return next;
    });
  };

  return (
    <section id="top" ref={ref} className="relative min-h-[100vh] flex flex-col overflow-hidden hero-wash">
      {/* Animated gradient orbs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <motion.div
          className="absolute rounded-full"
          style={{
            width: 700,
            height: 700,
            top: "-10%",
            left: "-5%",
            background: "radial-gradient(circle, rgba(228,199,122,0.15), transparent 60%)",
            filter: "blur(50px)"
          }}
          animate={{ x: [0, 50, 0], y: [0, 30, 0] }}
          transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute rounded-full"
          style={{
            width: 500,
            height: 500,
            bottom: "10%",
            right: "-5%",
            background: "radial-gradient(circle, rgba(184,115,51,0.12), transparent 60%)",
            filter: "blur(60px)"
          }}
          animate={{ x: [0, -30, 0], y: [0, -20, 0] }}
          transition={{ duration: 25, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      {/* Background Video with Vignette */}
      <motion.div style={{ y: yBg }} className="absolute inset-0 z-0">
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className="w-full h-full object-cover brightness-[0.45]"
        >
          {/* <source src="https://videos.pexels.com/video-files/3129671/3129671-uhd_2560_1440_30fps.mp4" type="video/mp4" /> */}
          <source src="https://videos.pexels.com/video-files/36037664/15283442_1920_1080_24fps.mp4" type="video/mp4" />
        </video>
        <div
          className="absolute inset-0"
          style={{
            background: "linear-gradient(180deg, rgba(10,9,8,0.5) 0%, rgba(10,9,8,0.9) 100%)"
          }}
        />
      </motion.div>

      <Particles />

      {/* Main Content Area */}
      <div className="container-xl relative z-10 flex-1 flex flex-col pt-32 md:pt-44 pb-20 md:pb-32 px-4 lg:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-8 lg:gap-20 items-center flex-1">

          {/* Left Side: Messaging */}
          <motion.div
            style={{ opacity }}
            className="flex flex-col justify-center order-2 lg:order-1"
          >
            <h1 className="font-display text-[clamp(36px,8.5vw,108px)] leading-[0.95] tracking-tight text-[var(--cream)] balance">
              <span className="block mb-2 md:mb-4">
                <SplitTitle text="Stories" delay={0.5} /> <SplitTitle text="Crafted." delay={0.7} italic />
              </span>
              <span className="block">
                <span className="gold-text"><SplitTitle text="Brands" delay={0.9} /></span> <SplitTitle text="Created." delay={1.1} italic />
              </span>
            </h1>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.3 }}
              className="mt-6 md:mt-10 max-w-2xl text-[var(--cream-muted)] text-[16px] md:text-[18px] leading-relaxed balance font-display italic space-y-4"
            >
              <p>
                A multidisciplinary creative studio blending storytelling, branding, media, and digital innovation to build work that resonates.
              </p>
              <p>
                At Meraki Solutions, we believe the strongest brands are built through clarity, emotion, and experience. We bring together strategy, storytelling, design, media, and technology to create meaningful work that connects with people and leaves a lasting impact.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="mt-8 md:mt-12 flex flex-wrap items-center gap-4 md:gap-6"
            >
              <Link to="/contact" className="btn-primary scale-90 md:scale-100 origin-left" data-cursor="start">
                Start a project
                <ArrowRight size={18} />
              </Link>
              <a href="#portfolio" className="btn-ghost scale-90 md:scale-100 origin-left" data-cursor="scroll">
                View Work
              </a>
            </motion.div>
          </motion.div>

          {/* Right Side: Stationwithchitra (Workstation3D) */}
          <motion.div
            initial={{ opacity: 0, scale: 0.7, y: 20 }}
            animate={{ opacity: 1, scale: 1.15, y: 0 }}
            transition={{ duration: 1.2, delay: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="relative order-1 lg:order-2 max-w-[320px] md:max-w-none mx-auto lg:mx-0 w-full lg:scale-100"
          >
            {/* Branding Tag */}
            <div className="absolute -top-10 lg:-top-6 right-0 z-20 flex items-center gap-3">
              <div className="h-px w-8 md:w-12 bg-[var(--gold)] opacity-30" />
              <span className="font-impact text-[9px] md:text-[10px] tracking-[0.4em] text-[var(--gold)] uppercase whitespace-nowrap">
                STUDIO CINEMA RIG
              </span>
            </div>

            <div className="relative z-10 group">
              <StudioCamera3D tilt={tilt} />
            </div>

            {/* Background elements - hidden on small mobile to save space */}
            <div className="absolute -inset-10 z-0 pointer-events-none opacity-20 hidden lg:block">
              <svg viewBox="0 0 400 400" fill="none" className="w-full h-full">
                <circle cx="200" cy="200" r="160" stroke="var(--gold)" strokeWidth="0.5" strokeDasharray="4 8" />
                <circle cx="200" cy="200" r="120" stroke="var(--gold)" strokeWidth="0.2" />
              </svg>
            </div>
          </motion.div>

        </div>
      </div>

      {/* 3D Controls - Repositioned to middle-right on mobile */}
      <div className="absolute top-[48%] -translate-y-1/2 right-4 md:top-auto md:bottom-32 md:right-10 md:translate-y-0 z-50">
        <DpadControls onTilt={handleManualTilt} onSpin={() => setTilt(prev => ({ ...prev, ry: prev.ry + 360 }))} />
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.2, delay: 2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 text-[var(--muted)] text-[10px] uppercase tracking-[0.3em]"
      >
        <div className="w-[1px] h-12 bg-gradient-to-b from-[var(--gold)] to-transparent" />
        scroll
      </motion.div>

      {/* Marquee remains at the very bottom */}
      <div className="relative z-10 border-t border-[rgba(201,169,97,0.15)] py-6 overflow-hidden bg-[rgba(10,9,8,0.6)] backdrop-blur-md w-full max-w-[100vw]">
        <div className="marquee-track">
          {[...heroMarquee, ...heroMarquee].map((t, i) => (
            <div key={i} className="flex items-center gap-12">
              <span className="font-display italic text-2xl md:text-3xl text-[var(--cream)]">
                {t}
              </span>
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--gold)]" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const DpadControls = ({ onTilt, onSpin }) => {
  const { ChevronUp, ChevronDown, ChevronLeft, ChevronRight, RotateCw, RefreshCcw } = require("lucide-react");
  return (
    <div className="flex flex-col items-center gap-1 bg-black/40 p-2 rounded-2xl backdrop-blur-xl border border-white/10 shadow-[0_20px_50px_rgba(0,0,0,0.5)]">
      <button
        onClick={() => onTilt("up")}
        className="w-10 h-10 rounded-xl flex items-center justify-center bg-black/40 border border-white/10 hover:border-[var(--gold)] hover:bg-[var(--gold)]/10 transition-all text-[var(--cream-muted)] hover:text-[var(--gold)]"
      >
        <ChevronUp size={20} />
      </button>
      <div className="flex gap-1">
        <button
          onClick={() => onTilt("left")}
          className="w-10 h-10 rounded-xl flex items-center justify-center bg-black/40 border border-white/10 hover:border-[var(--gold)] hover:bg-[var(--gold)]/10 transition-all text-[var(--cream-muted)] hover:text-[var(--gold)]"
        >
          <ChevronLeft size={20} />
        </button>
        <button
          onClick={() => onTilt("reset")}
          className="w-10 h-10 rounded-xl flex items-center justify-center bg-[var(--gold)]/10 border border-[var(--gold)]/30 hover:border-[var(--gold)] hover:bg-[var(--gold)]/20 transition-all text-[var(--gold)]"
        >
          <RefreshCcw size={16} />
        </button>
        <button
          onClick={() => onTilt("right")}
          className="w-10 h-10 rounded-xl flex items-center justify-center bg-black/40 border border-white/10 hover:border-[var(--gold)] hover:bg-[var(--gold)]/10 transition-all text-[var(--cream-muted)] hover:text-[var(--gold)]"
        >
          <ChevronRight size={20} />
        </button>
      </div>
      <button
        onClick={() => onTilt("down")}
        className="w-10 h-10 rounded-xl flex items-center justify-center bg-black/40 border border-white/10 hover:border-[var(--gold)] hover:bg-[var(--gold)]/10 transition-all text-[var(--cream-muted)] hover:text-[var(--gold)]"
      >
        <ChevronDown size={20} />
      </button>

      <div className="mt-2 w-full">
        <button
          onClick={onSpin}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-[var(--gold)]/10 border border-[var(--gold)]/20 hover:bg-[var(--gold)]/20 transition-all"
        >
          <RotateCw size={12} className="text-[var(--gold)]" />
          <span className="font-impact text-[9px] tracking-[0.1em] text-[var(--gold)] uppercase">360 Spin</span>
        </button>
      </div>
    </div>
  );
};

const Particles = () => {
  // Small drifting dots
  const dots = Array.from({ length: 18 }).map((_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    s: 1 + Math.random() * 2,
    d: 6 + Math.random() * 10,
    delay: Math.random() * 5
  }));
  return (
    <div className="absolute inset-0 pointer-events-none">
      {dots.map((d) => (
        <motion.span
          key={d.id}
          className="absolute rounded-full"
          style={{
            top: `${d.y}%`,
            left: `${d.x}%`,
            width: d.s,
            height: d.s,
            background: "#e4c77a",
            boxShadow: "0 0 6px #e4c77a",
            opacity: 0.5
          }}
          animate={{
            y: [-20, 20, -20],
            x: [-10, 10, -10],
            opacity: [0.2, 0.7, 0.2]
          }}
          transition={{
            duration: d.d,
            repeat: Infinity,
            ease: "easeInOut",
            delay: d.delay
          }}
        />
      ))}
    </div>
  );
};

const SplitTitle = ({ text, delay = 0, italic = false }) => {
  return (
    <motion.span
      initial={{ y: "40%", opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1.2, delay: delay, ease: [0.22, 1, 0.36, 1] }}
      className={italic ? "italic font-display inline-block" : "inline-block"}
      style={{ whiteSpace: "pre" }}
    >
      {text}
    </motion.span>
  );
};

export default Hero;
